

















































































import type { EmojiIndex } from 'emoji-mart-vue-fast';
import { store } from 'emoji-mart-vue-fast';
import type { PropType, Ref } from '@vue/composition-api';
import type { Emoji } from '@getaccept/lib-ui';
import { computed, defineComponent, nextTick, onMounted, ref } from '@vue/composition-api';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { EmojiHelper } from '../../emoji/helpers/emoji.helper';
import { EMOJI_INLINE_TRIGGER_COUNT } from '../../emoji/constants/emoji.constants';
import { ChatHelper } from '../helpers/chat.helper';

export default defineComponent({
  props: {
    isPreview: {
      type: Boolean,
    },
    emojisEnabled: {
      type: Boolean,
    },
    emojiIndex: {
      type: Object as PropType<EmojiIndex>,
      default: null,
    },
  },
  setup(props, { emit }) {
    const inputRef: Ref<HTMLTextAreaElement> = ref(null);
    const text = ref('');
    const skinTone = ref(0);
    const emojiSearch = ref('');
    const selectedInlineEmojiIndex = ref(0);
    const showSendButtonTooltip = ref(false);
    const showEmojis = ref(false);
    const showInlineEmojiMenu = ref(false);
    const tooltipDelay = ref(3000);

    const matchedEmojis = computed((): Emoji[] => {
      if (!props.emojiIndex) return [];
      return EmojiHelper.getEmojiListByKey(emojiSearch.value, props.emojiIndex);
    });

    const correctEmojiSearch = computed(
      () =>
        emojiSearch.value.charAt(0) === ':' &&
        emojiSearch.value.length >= EMOJI_INLINE_TRIGGER_COUNT &&
        matchedEmojis.value.length > 0 &&
        !emojiSearch.value.includes(' ')
    );

    const handleSkinChange = (newSkinTone: number) => {
      skinTone.value = newSkinTone;
    };

    const isEndingColon = (text: string) => text.charAt(text.length - 1) === ':';

    const handleInput = () => {
      if (!props.emojisEnabled) {
        return;
      }
      emojiSearch.value = getEmojiKey(inputRef.value, text.value);
      const isEndingWithColon = isEndingColon(emojiSearch.value);
      selectedInlineEmojiIndex.value = 0;

      if (isEndingWithColon && correctEmojiSearch.value) {
        const emoji = props.emojiIndex.findEmoji(emojiSearch.value);
        replaceEmojiSearchWithEmoji(emoji);
        return;
      }

      if (correctEmojiSearch.value && !showInlineEmojiMenu.value) {
        setShowInlineEmojiMenu(true);
        return;
      }
      if (!correctEmojiSearch.value) {
        resetInlineEmojiMenu();
      }
    };

    const getEmojiKey = (element: HTMLTextAreaElement, text: string) => {
      const end: number = element.selectionEnd;
      const start: number = text.lastIndexOf(':', end - 2);
      return start >= 0 ? text.substring(start, end) : '';
    };

    const resetInlineEmojiMenu = () => {
      setShowInlineEmojiMenu(false);
      emojiSearch.value = '';
      selectedInlineEmojiIndex.value = 0;
    };

    const setShowInlineEmojiMenu = (show: boolean) => {
      showInlineEmojiMenu.value = show;
    };

    const setShowEmojiPicker = (show: boolean) => {
      showEmojis.value = show;
    };

    const handleSendButtonTooltip = () => {
      if (showSendButtonTooltip.value) {
        return;
      }
      showSendButtonTooltip.value = true;
      setTimeout(() => {
        showSendButtonTooltip.value = false;
      }, tooltipDelay.value);
    };

    const handleAddEmoji = async (emoji: Emoji) => {
      const cursorPosition: number = inputRef.value?.selectionEnd;
      setShowEmojiPicker(false);
      text.value =
        text.value.slice(0, cursorPosition) + emoji.native + text.value.slice(cursorPosition);
      inputRef.value?.focus();
      await nextTick();
      inputRef.value?.setSelectionRange(
        cursorPosition + emoji.native.length,
        cursorPosition + emoji.native.length
      );
    };

    const replaceEmojiSearchWithEmoji = async (emoji: Emoji) => {
      const nativeEmoji: string = EmojiHelper.getEmojiWithSkinTone(emoji, skinTone.value)?.native;
      if (!nativeEmoji) {
        resetInlineEmojiMenu();
        return;
      }
      const cursorPosition: number =
        inputRef.value?.selectionEnd - emojiSearch.value.length + nativeEmoji.length;
      text.value = text.value.replace(emojiSearch.value, nativeEmoji);
      resetInlineEmojiMenu();
      await nextTick();
      inputRef.value?.setSelectionRange(cursorPosition, cursorPosition);
    };

    const handleSend = () => {
      if (props.isPreview) {
        handleSendButtonTooltip();
        return;
      }
      setFocus();
      if (text.value) {
        emit('send', EmojiHelper.encode(text.value));
        text.value = '';
      }
    };

    const setFocus = () => {
      inputRef.value?.focus();
    };

    const clickOutside = () => {
      if (!showEmojis.value) {
        return;
      }
      setShowEmojiPicker(false);
    };

    const handleEnter = () => {
      if (showInlineEmojiMenu.value) {
        replaceEmojiSearchWithEmoji(matchedEmojis.value[selectedInlineEmojiIndex.value]);
      } else {
        handleSend();
      }
    };

    const handleTab = (event: KeyboardEvent) => {
      if (showInlineEmojiMenu.value) {
        event.preventDefault();
        replaceEmojiSearchWithEmoji(matchedEmojis.value[selectedInlineEmojiIndex.value]);
      }
    };

    const handleNavigate = (event: KeyboardEvent) => {
      if (!showInlineEmojiMenu.value) {
        return;
      }
      event.preventDefault();
      const firstSelected: boolean = selectedInlineEmojiIndex.value === 0;
      const lastSelected: boolean =
        selectedInlineEmojiIndex.value === matchedEmojis.value.length - 1;

      let step: number;
      switch (event.key) {
        case 'ArrowUp':
          if (firstSelected) {
            return;
          }
          step = -1;
          break;
        case 'ArrowDown':
          if (lastSelected) {
            return;
          }
          step = 1;
          break;
      }

      selectedInlineEmojiIndex.value += step;
    };

    onMounted(() => {
      skinTone.value = store.get('skin');
    });

    return {
      inputRef,
      resetInlineEmojiMenu,
      showInlineEmojiMenu,
      text,
      handleInput,
      handleNavigate,
      handleEnter,
      handleTab,
      matchedEmojis,
      skinTone,
      selectedInlineEmojiIndex,
      replaceEmojiSearchWithEmoji,
      clickOutside,
      showEmojis,
      setShowEmojiPicker,
      getEmojiLocalization: ChatHelper.getEmojiLocalization(),
      handleAddEmoji,
      handleSkinChange,
      showSendButtonTooltip,
      handleSend,
      t,
      correctEmojiSearch,
      emojiSearch,
      getEmojiKey,
      setShowInlineEmojiMenu,
      handleSendButtonTooltip,
      setFocus,
      isEndingColon,
    };
  },
});
